import { TileType } from '@/core/models/tile-type';
import { TimeBudget, TimeBudgetUpdateRequestDto } from '@/core/models/time-budget';

const SECONDS_PER_HOUR = 3600;

/** What saving a tile's budget settings has to send to the API. */
export type TimeBudgetChange =
    | { type: 'none' }
    | { type: 'create'; budgetSeconds: number }
    | { type: 'update'; changes: TimeBudgetUpdateRequestDto };

/** The API stores budgets in whole seconds, the UI works in hours with decimals. */
export class TimeBudgetUtils {
    // the API only checks for at least 1 s; the budgetSeconds column (INT UNSIGNED) can't hold more than MAX_SECONDS
    public static readonly MIN_SECONDS = 1;
    public static readonly MAX_SECONDS = 4_294_967_295;
    public static readonly MAX_HOURS = Math.floor(TimeBudgetUtils.MAX_SECONDS / SECONDS_PER_HOUR);
    // decimals of the hours the UI shows and accepts
    public static readonly HOURS_FRACTION_DIGITS = 2;
    // one formatter per language; formatHours() runs for every bar on every change detection
    private static readonly hourFormats = new Map<string, Intl.NumberFormat>();
    // rounds like the hours field, which formats with Intl.NumberFormat (toFixed rounds e.g. 1.005 differently)
    private static readonly shownHoursFormat = new Intl.NumberFormat('en-US', {
        maximumFractionDigits: TimeBudgetUtils.HOURS_FRACTION_DIGITS,
        useGrouping: false,
    });

    public static hoursToSeconds(hours: number): number {
        return Math.round(hours * SECONDS_PER_HOUR);
    }

    public static secondsToHours(seconds: number): number {
        return seconds / SECONDS_PER_HOUR;
    }

    /** e.g. 9000 s => "2.5" (en) or "2,5" (de) */
    public static formatHours(seconds: number, locale: string | undefined): string {
        const language = locale || 'en';
        let format = TimeBudgetUtils.hourFormats.get(language);
        if (format === undefined) {
            format = new Intl.NumberFormat(language, { maximumFractionDigits: TimeBudgetUtils.HOURS_FRACTION_DIGITS });
            TimeBudgetUtils.hourFormats.set(language, format);
        }
        return format.format(TimeBudgetUtils.secondsToHours(seconds));
    }

    /** All tracked time of the budget, e.g. of a task under every customer of its project. */
    public static usedSecondsTotal(budget: TimeBudget): number {
        return budget.usage.reduce((sum, row) => sum + row.usedSeconds, 0);
    }

    /** Only tiles of the top layer and tiles assigned to exactly one parent can have a budget, not global ones. */
    public static isAllowedFor(tile: { type: TileType; parents: readonly unknown[] }, layerIndex: number): boolean {
        return layerIndex === 1 || (tile.type !== TileType.Global && tile.parents.length === 1);
    }

    /** Whether the hours convert to an amount the API accepts. */
    public static isValidHours(hours: number | null | undefined): hours is number {
        if (hours === null || hours === undefined || !Number.isFinite(hours)) {
            return false;
        }
        const seconds = TimeBudgetUtils.hoursToSeconds(hours);
        return seconds >= TimeBudgetUtils.MIN_SECONDS && seconds <= TimeBudgetUtils.MAX_SECONDS;
    }

    /**
     * What saving the dialog's budget settings has to send. Switching a budget off keeps it, so switching it on
     * again reuses it instead of creating a second one.
     */
    public static getChange(
        existing: TimeBudget | undefined,
        enabled: boolean,
        hours: number | null,
    ): TimeBudgetChange {
        if (!enabled) {
            return existing?.active ? { type: 'update', changes: { active: false } } : { type: 'none' };
        }
        if (!TimeBudgetUtils.isValidHours(hours)) {
            return { type: 'none' };
        }
        const budgetSeconds = TimeBudgetUtils.hoursToSeconds(hours);
        if (existing === undefined) {
            return { type: 'create', budgetSeconds };
        }
        const changes: TimeBudgetUpdateRequestDto = {};
        // the hours field hands back its rounded amount once it was focused: an amount stored with more decimals
        // (e.g. set through the API) is only changed when another amount is entered
        if (
            budgetSeconds !== existing.budgetSeconds &&
            budgetSeconds !== TimeBudgetUtils.toShownSeconds(existing.budgetSeconds)
        ) {
            changes.budgetSeconds = budgetSeconds;
        }
        if (!existing.active) {
            changes.active = true;
        }
        return Object.keys(changes).length > 0 ? { type: 'update', changes } : { type: 'none' };
    }

    /** The amount as the hours field shows it, e.g. 1000 s (0.2777… h) => 1008 s (0.28 h). */
    private static toShownSeconds(seconds: number): number {
        const hours = TimeBudgetUtils.shownHoursFormat.format(TimeBudgetUtils.secondsToHours(seconds));
        return TimeBudgetUtils.hoursToSeconds(Number(hours));
    }
}
